import React, { useState, useRef, useEffect } from 'react';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';
import { SlGlobe } from 'react-icons/sl';
import BaseImage from './base-image';
import AppInputError from './input-error';
import Loader from './loader';
import { FiSearch } from 'react-icons/fi';

interface DropdownProps {
  id?: string;
  label?: string;
  options: any[];
  icon?: React.ReactNode;
  selectedValue: string;
  onSelectChange: (value: any) => void;
  required?: boolean;
  emitObject?: boolean;
  displaySelector?: 'name' | 'label';
  showCountryFlag?: boolean;
  errorMessage?: string;
  loading?: boolean;
  isUsedForExchangeInput?: boolean;
  renderItem?: (option: any) => React.ReactNode;
  disabled?: boolean;
  placeholder?: string; // ✅ new optional prop

}

const CustomDropdown: React.FC<DropdownProps> = ({
  id,
  label,
  options,
  icon,
  selectedValue,
  onSelectChange,
  required,
  emitObject = false,
  displaySelector = 'label',
  showCountryFlag = false,
  errorMessage,
  loading = false,
  isUsedForExchangeInput = false,
  renderItem,
  disabled,
  placeholder
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [flag, setCountryFlag] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [displayName, setDisplayName] = useState(selectedValue || ''); // Initialize with selectedValue
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter options based on the search term
  const filteredOptions = options.filter((option) =>
    option.label
      ? option.label
          .toLowerCase()
          .match(searchTerm.replace(/\\/g, '').toLowerCase())
      : '',
  );

  const handleSelect = (
    labelValue: string,
    nameValue: string,
    countryFlag?: string,
  ) => {
    onSelectChange(labelValue); // Pass the label to the parent
    setDisplayName(displaySelector === 'label' ? labelValue : nameValue); // Set the display name
    setSearchTerm(''); // Clear the search input
    setIsDropdownOpen(false); // Close the dropdown
    setCountryFlag(countryFlag || ''); // Set the country flag
  };

  const handleObjectSelect = (option: any) => {
    onSelectChange(option); // Pass the label to the parent
    setDisplayName(option[displaySelector]); // Set the display name
    setSearchTerm(''); // Clear the search input
    setIsDropdownOpen(false); // Close the dropdown
    setCountryFlag(option.countryFlag || ''); // Set the country flag
  };

  // Close the dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false); // Close the dropdown if clicked outside
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!selectedValue) {
      if (displayName) {
        setDisplayName('');
      }
      return;
    }

    const selectedOption = options.find(
      (option) => option.name === selectedValue,
    );

    if (selectedOption) {
      setDisplayName((prev) =>
        prev !==
        (displaySelector === 'label'
          ? selectedOption.label
          : selectedOption.name)
          ? displaySelector === 'label'
            ? selectedOption.label
            : selectedOption.name
          : prev,
      );

      setCountryFlag((prev) =>
        prev !== selectedOption.countryFlag
          ? selectedOption.countryFlag || ''
          : prev,
      );
    }
  }, [selectedValue]);

  return (
    <div id={`${id}-dropdown`} className="relative w-full" ref={dropdownRef}>
      {label && (
        <div className="mb-1 flex items-center gap-2">
          <p className="font-semibold text-xs">{label}</p>
          <p className={`text-red-500 ${required ? '' : 'invisible'}`}>*</p>
        </div>
      )}

      <div
        className={`flex items-center rounded-xl px-4 py-2 h-12 transition-colors cursor-pointer gap-x-2 ${
          errorMessage ? 'border-red-500 bg-red-50' : ''
        } ${isUsedForExchangeInput ? '' : 'bg-[#FAFAFA]'}`}
        onClick={(e) => {
          if (!disabled && !loading) {
            e.stopPropagation();
            setIsDropdownOpen(true);
          }
        }}
      >
        {showCountryFlag && displayName && !loading ? (
          <BaseImage
            imgSrc={flag}
            imgAlt={displayName}
            imgClass="w-6 min-w-[1.5rem] max-w-[1.5rem] h-6 rounded-full mr-2 object-cover"
          />
        ) : (
          icon && <SlGlobe className="text-gray-400 mr-4 text-[20px]" />
        )}
        <div className="w-full text-sm">
          {displayName.length ? (
            displayName
          ) : (
            <span className="text-gray-400">{label}</span>
          )}
        </div>
        {loading ? (
          <Loader
            width="1.2rem"
            borderTopColor="border-t-primary"
            className="shrink-0"
          />
        ) : isDropdownOpen ? (
          <BsChevronUp
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="text-gray-400 shrink-0"
          />
        ) : (
          <BsChevronDown
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="text-gray-400 shrink-0"
          />
        )}
      </div>

      {errorMessage && <AppInputError text={errorMessage} />}

      {isDropdownOpen && (
        <ul className="absolute z-10 w-full bg-white border border-[#E4F1FF] rounded-xl max-h-60 overflow-y-auto mt-1 shadow-lg">
          <li className="px-3 py-3 cursor-pointer text-sm w-full">
            <div className={`flex items-center border rounded-xl py-2 px-3`}>
              <FiSearch className="mr-2 text-gray-500" />
              <input
                type="text"
                placeholder={'Search'}
                className="flex-1 outline-none text-sm placeholder-[#54555A] placeholder:font-light placeholder:text-sm"
                value={searchTerm !== '' ? searchTerm : ''}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                autoComplete="off"
              />
            </div>
          </li>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={index}
                className="px-4 py-3 cursor-pointer hover:bg-blue-100 text-sm w-full"
                onClick={() =>
                  emitObject
                    ? handleObjectSelect(option)
                    : handleSelect(
                        displaySelector === 'label'
                          ? option.label
                          : option.name,
                        option.name,
                        option.countryFlag,
                      )
                }
              >
                {renderItem ? (
                  renderItem(option)
                ) : (
                  <div className="flex items-center gap-2 ">
                    {showCountryFlag && (
                      <BaseImage
                        imgSrc={option.countryFlag || ''}
                        imgAlt={option.name}
                        imgClass="w-6 min-w-[1.5rem] max-w-[1.5rem] h-6 rounded-full mr-2 object-cover"
                      />
                    )}
                    <p className="w-full">
                      {displaySelector === 'name' ? option.name : option.label}
                    </p>
                  </div>
                )}
              </li>
            ))
          ) : (
            <li className="px-4 py-2 text-gray-500 text-sm">
              No options found
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default CustomDropdown;
